import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import SearchIcon from "@mui/icons-material/Search";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import {
  getNotificationPreferences,
  setNotificationPreference,
} from "../../../fetchers/cmmsNotificationPreference";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import { CmmsEmptyState, CmmsErrorState, formatCmmsError } from "../../cmms/components/CmmsStates";
import {
  getAssetHierarchyPalette,
  getSwitchSx,
} from "../../cmms/assetHierarchyStyles";
import messages from "../messages";
import NotificationPreferencesDataGrid from "./NotificationPreferencesDataGrid";

/*
 * The notification preferences tab: who is emailed about work orders, one switch per person.
 *
 * StorageLocationsPanel's shape minus the dialog and the confirmation: there is nothing to create
 * and nothing to destroy here, only one flag per person to flip.
 *
 * No companyKey. It is the one panel on this page that does not read it, because the server takes
 * the tenant from the signed-in user rather than from the request — the grid is already scoped by
 * the time it arrives here. See the fetcher.
 *
 * Nothing here decides what "enabled" means when a user has no row. The server resolves that and
 * sends the answer; a default applied a second time here is the copy that goes wrong.
 */
export default function NotificationPreferencesPanel() {
  const theme = useTheme();
  const intl = useIntl();
  const queryClient = useQueryClient();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);

  const [showAllUsers, setShowAllUsers] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [pendingUserIds, setPendingUserIds] = useState([]);

  const preferencesQuery = useQuery(
    ["cmmsNotificationPreferences", showAllUsers],
    () => getNotificationPreferences(!showAllUsers),
    { staleTime: 30000 },
  );

  const preferences = useMemo(() => preferencesQuery.data ?? [], [preferencesQuery.data]);

  // The same division StorageLocationsPanel draws: the endpoint owns which rows exist — that is
  // what the switch below asks it — and the browser owns which of them are on screen.
  //
  // Departments are in the needle because "who in Formsprutning gets mail" is the question this tab
  // exists to answer, and the column alone leaves it to be found by eye down a long list.
  const visiblePreferences = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    if (!needle) return preferences;
    return preferences.filter((row) => {
      const departments = (row.departments ?? []).map((department) => department.name).join(" ");
      return `${row.name ?? ""} ${row.username ?? ""} ${row.email ?? ""} ${departments}`
        .toLowerCase()
        .includes(needle);
    });
  }, [preferences, searchText]);

  const toggleMutation = useMutation({
    mutationFn: ({ userId, emailEnabled }) => setNotificationPreference(userId, emailEnabled),
  });

  const hasActiveFilters = showAllUsers || searchText.trim() !== "";

  const handleClearFilters = () => {
    setShowAllUsers(false);
    setSearchText("");
  };

  const handleToggle = async (row) => {
    const next = !row.emailEnabled;

    // Per row rather than a page-wide backdrop: the rows are independent, and an admin working
    // down a list should not have the whole grid freeze between each one.
    setPendingUserIds((ids) => [...ids, row.userId]);

    try {
      await toggleMutation.mutateAsync({ userId: row.userId, emailEnabled: next });
      await queryClient.invalidateQueries({ queryKey: ["cmmsNotificationPreferences"] });
      pushNotification(intl.formatMessage(next ? messages.toastEnabled : messages.toastDisabled, {
        name: row.name || row.username || `#${row.userId}`,
      }));
    } catch (error) {
      // The switch never moved — it is driven by the query, not by local state — so a failure
      // needs no rollback, only saying so.
      pushErrorMessage(formatCmmsError(error, intl.formatMessage(messages.toggleFailed)));
    } finally {
      setPendingUserIds((ids) => ids.filter((id) => id !== row.userId));
    }
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        <FormattedMessage {...messages.notificationsSubtitle} />
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ gap: 1, mb: 2 }}>
        <TextField
          size="small"
          sx={{ width: { xs: "100%", sm: 280 } }}
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          label={intl.formatMessage(messages.filterSearch)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {/*
          * Server-side, and the only way to anyone outside the technician tier. The department
          * fan-out mails technicians, so they are the list that matters — but a work order can be
          * dispatched to somebody personally whatever their role, and without this switch that
          * person could never be switched off.
          */}
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showAllUsers}
              onChange={(event) => setShowAllUsers(event.target.checked)}
              sx={getSwitchSx(cmmsColors)}
            />
          }
          label={intl.formatMessage(messages.filterShowAllUsers)}
        />
      </Stack>

      {preferencesQuery.error &&
        <CmmsErrorState
          error={preferencesQuery.error}
          fallback={intl.formatMessage(messages.notificationsLoadFailed)}
          onRetry={() => preferencesQuery.refetch()}
          isRetrying={preferencesQuery.isFetching}
        />
      }
      {!preferencesQuery.error &&
        <NotificationPreferencesDataGrid
          preferences={visiblePreferences}
          // First load only, so a background refetch does not replace the list with a skeleton.
          isLoading={preferencesQuery.isLoading}
          pendingUserIds={pendingUserIds}
          onToggle={handleToggle}
          emptyState={
            hasActiveFilters
              ? <CmmsEmptyState
                  icon={<FilterAltOffIcon />}
                  title={intl.formatMessage(messages.emptyFilteredTitle)}
                  hint={intl.formatMessage(messages.emptyFilteredHint)}
                  action={
                    <Button size="small" variant="outlined" color="secondary" onClick={handleClearFilters}>
                      <FormattedMessage {...messages.filterClear} />
                    </Button>
                  }
                />
              : <CmmsEmptyState
                  icon={<MarkEmailReadIcon />}
                  title={intl.formatMessage(messages.notificationsEmptyTitle)}
                  hint={intl.formatMessage(messages.notificationsEmptyHint)}
                />
          }
        />
      }
    </Box>
  );
}
